import { canonicalMatchHash } from './match/compatibility.mjs';
// Canonieke route-helpers voor de hash-router van de app-shell.
//
// Doel: EEN plek die weet hoe een record-hash eruitziet (#crm-<entity>-<slug>,
// #proces-<id>) en bij welke werkruimte een recordtype hoort. De tab-hashes zelf
// blijven van lib/app-navigation.mjs - deze module leidt ze daaruit af en kopieert
// die strings NIET. Geen React, geen DOM op module-niveau: unit-testbaar.
//
// Interne vorm van een hash: GEEN leidende slash ('#proces-1'). Voor een echte
// <a href> gebruik je recordHref(), die '/'-prefixt ('/#proces-1'). Call sites in
// de app bouwen historisch beide vormen, dus elke vergelijking loopt via
// normalizeHash().
//
// pbRoute = de route-metadata die in history.state meereist:
//   {
//     hash,          // canonieke hash van deze entry ('#proces-1', '#zoeken-kandidaten', …)
//     kind,          // 'workspace' | 'record' | 'process'
//     originHash,    // hash van de werkruimte waar deze entry vandaan kwam (of null)
//     depth,         // 0 = werkruimte, 1+ = hoe diep in de detail-keten
//     label,         // leesbaar label van deze entry
//     parentLabel,   // leesbaar label van de bovenliggende entry (of null)
//     originLabel,   // leesbaar label van de werkruimte van herkomst (of null)
//     seq,           // positief volgnummer: positie in de history-lijst (zie nextRouteSeq)
//   }
// Een entry ZONDER pbRoute heeft geen seq; lees dat als onbekend, nooit als 0.
// history.state draagt al { pbModal } (lib/modal-history.mjs); withRoute MERGET
// daarom altijd en vervangt de state nooit.

import { isClientType } from './crm/client-type.mjs';
import { CRM_NAV_HASH } from './app-navigation.mjs';
import { functieIdentity, functieWhitespace } from './functie.mjs';
import { buildSearchHash, FUNCTIE_TAB } from './search/preset-url.mjs';

export const PB_ROUTE_STATE_KEY = 'pbRoute';

const CRM_RECORD_ENTITIES = Object.freeze(['candidate', 'company', 'contact', 'job']);
// De sollicitant (13 okt 2026) is een recordvenster met een eigen hash
// (#crm-applicant-<id>), maar GEEN CRM-werkruimte: geen raster, geen zoekgroep,
// geen WORKSPACE_KEY. Daarom staat hij hier los van CRM_RECORD_ENTITIES.
const RECORD_HASH_ENTITIES = Object.freeze([...CRM_RECORD_ENTITIES, 'applicant']);
const RECORD_HASH_RE = /^#crm-(candidate|company|contact|job|applicant)-(.+)$/;

// Werkruimte per recordtype. De hash-strings komen uit CRM_NAV_HASH (single
// source of truth); hier staat alleen de mapping recordtype -> CRM_NAV_HASH-key.
const WORKSPACE_KEY = Object.freeze({
  process: 'processen',
  candidate: 'candidates',
  job: 'jobs',
  contact: 'contacts',
  company: 'clients',        // elk getekend klanttype (client + contracting)
  companyProspect: 'prospects',
});

const WORKSPACE_LABEL = Object.freeze({
  processen: 'Processen',
  candidates: 'Kandidaten',
  jobs: 'Jobs',
  contacts: 'Contacten',
  clients: 'Klanten',
  prospects: 'Prospects',
});

// '' en '#' worden beide '' (= geen fragment, Vandaag). Alle andere waarden
// krijgen exact EEN leidende '#', zonder leidende '/'.
export function normalizeHash(h) {
  let s = String(h == null ? '' : h);
  s = s.replace(/^\/+/, '');
  s = s.replace(/^#+/, '');
  return s ? `#${s}` : '';
}

function encPart(slugOrId) {
  return encodeURIComponent(String(slugOrId == null ? '' : slugOrId));
}

// Canonieke record-hash. Interne vorm: GEEN leidende slash.
export function recordHash(entity, slugOrId) {
  const e = String(entity || '');
  if (slugOrId == null || String(slugOrId) === '') return null;
  if (e === 'process') return `#proces-${encPart(slugOrId)}`;
  if (RECORD_HASH_ENTITIES.includes(e)) return `#crm-${e}-${encPart(slugOrId)}`;
  // De bellijst-deep link (#cl-<id>) is de bestaande vorm die de deep-link-
  // effecten in app/page.js en components/HomeV2/CallLists.js al schrijven en
  // lezen; dit is nu de ene canonieke builder ervoor, geen nieuwe vorm. De
  // lezer is parseCallListHash, NIET parseRecordHash: zie daar.
  if (e === 'callList') return `#cl-${encPart(slugOrId)}`;
  // Een gedeeld sjabloon heeft geen eigen pagina: de link opent de Sjablonen
  // (lib/app-navigation.mjs, tab templates). Beheer > Wijzigingen linkt zo.
  if (e === 'template') return '#templates';
  return null;
}

// Zelfde hash, maar als echte href ('/'-prefix) voor <a href>.
export function recordHref(entity, slugOrId) {
  const h = recordHash(entity, slugOrId);
  return h ? `/${h}` : null;
}

// Inverse van recordHash. Accepteert beide vormen ('#…' en '/#…').
export function parseRecordHash(h) {
  const s = normalizeHash(h);
  if (!s) return null;
  const proc = s.match(/^#proces-(.+)$/);
  if (proc) {
    try { return { entity: 'process', slug: decodeURIComponent(proc[1]) }; }
    catch { return { entity: 'process', slug: proc[1] }; }
  }
  const crm = s.match(RECORD_HASH_RE);
  if (crm) {
    try { return { entity: crm[1], slug: decodeURIComponent(crm[2]) }; }
    catch { return { entity: crm[1], slug: crm[2] }; }
  }
  return null;
}

// De bellijst-deep link (#cl-<id>) heeft BEWUST een eigen lezer en staat NIET
// in parseRecordHash (reviewronde 18 september 2026). app/page.js leest een
// lege parseRecordHash als 'deze hash is een werkruimte, leg hem vast als
// oorsprong': captureDetailOrigin en het hashchange-pad slaan de vastlegging
// over zodra parseRecordHash iets teruggeeft. Met #cl- erin verloor een proces
// dat je vanuit een open bellijst opent zijn oorsprong en viel de x terug op
// #zoeken, waarna CallLists.js de lijst onder de gebruiker dichtklapte. De
// bellijst is een werkruimte, geen record: CallLists.js stempelt hem zelf als
// kind 'workspace', depth 0.
export function parseCallListHash(h) {
  const s = normalizeHash(h);
  if (!s) return null;
  const cl = s.match(/^#cl-(.+)$/);
  if (!cl) return null;
  try { return { entity: 'callList', slug: decodeURIComponent(cl[1]) }; }
  catch { return { entity: 'callList', slug: cl[1] }; }
}

// ── Kwalificatieformulier (12 september 2026) ────────────────────────────────
// Het formulier is geen modal meer maar een volle-breedte pagina met een eigen
// hash. Vier vormen, in deze voorrang: een kandidaat (de actieve kwalificatie van
// die kandidaat), een job (via de job-slug), een bestaande rij op id, of een
// nieuw formulier. Eigen prefix, dus parseRecordHash raakt hem nooit.
export const QUAL_HASH_RE = /^#kwal-(candidate|job|id|new)(?:-(.+))?$/;
export const QUAL_ROUTE_LABEL = 'Kwalificatieformulier';

export function qualHash({ candidateSlug, jobSlug, qualId } = {}) {
  if (candidateSlug) return `#kwal-candidate-${encPart(candidateSlug)}`;
  if (jobSlug) return `#kwal-job-${encPart(jobSlug)}`;
  if (qualId) return `#kwal-id-${encPart(qualId)}`;
  return '#kwal-new';
}

export function qualHref(x) {
  return `/${qualHash(x)}`;
}

export function parseQualHash(h) {
  const s = normalizeHash(h);
  if (!s) return null;
  const m = s.match(QUAL_HASH_RE);
  if (!m) return null;
  if (m[1] === 'new') return { kind: 'new', key: '' };
  if (!m[2]) return null;
  try { return { kind: m[1], key: decodeURIComponent(m[2]) }; }
  catch { return { kind: m[1], key: m[2] }; }
}

// ── CV-pagina (13 oktober 2026) ─────────────────────────────────────
// De drie CV's van een kandidaat (P& CV met naam, anoniem, origineel bestand) zijn
// een volle-breedte pagina met een eigen hash, op dezelfde plek en laag als het
// kwalificatieformulier. Eigen prefix, dus parseRecordHash raakt hem nooit.
export const CV_HASH_RE = /^#cv-(named|anon|original)-(.+)$/;
export const CV_ROUTE_LABEL = 'CV';

export function cvHash(cvKind, slug) {
  return `#cv-${cvKind}-${encPart(slug)}`;
}

// { kind: 'cv', cvKind, key } zodat app/page.js hem in dezelfde paginastate kan
// dragen als een kwalificatieroute (kind is daar nooit 'cv').
export function parseCvHash(h) {
  const s = normalizeHash(h);
  if (!s) return null;
  const m = s.match(CV_HASH_RE);
  if (!m || !m[2]) return null;
  try { return { kind: 'cv', cvKind: m[1], key: decodeURIComponent(m[2]) }; }
  catch { return { kind: 'cv', cvKind: m[1], key: m[2] }; }
}

// ── Match (12 september 2026) ─────────────────────────────────────
// De matchingpagina heeft drie adressen: de kale pagina, een gekozen vacature en
// een gekozen kandidaat. Eigen prefix, dus parseRecordHash raakt hem nooit en een
// #match-hash opent GEEN recordvenster.
export const MATCH_HASH_RE = /^#match(?:-(job|candidate)-(.+))?$/;
export const MATCH_ROUTE_LABEL = 'Match';

export function matchHash({ jobSlug, candidateSlug } = {}) {
  if (jobSlug) return `#match-job-${encPart(jobSlug)}`;
  if (candidateSlug) return `#match-candidate-${encPart(candidateSlug)}`;
  return '#match';
}

export function matchHref(x) {
  return `/${matchHash(x)}`;
}

export function parseMatchHash(h) {
  const s = canonicalMatchHash(normalizeHash(h));
  if (!s) return null;
  const view = s.match(/^#match-(calls|map|insights|quality|longlist|leads|watch)$/);
  if (view) return { kind: view[1], key: '' };
  const m = s.match(MATCH_HASH_RE);
  if (!m) return null;
  if (!m[1]) return { kind: 'home', key: '' };
  if (!m[2]) return null;
  try { return { kind: m[1], key: decodeURIComponent(m[2]) }; }
  catch { return { kind: m[1], key: m[2] }; }
}

// ── Functietitel (4 oktober 2026, unit J) ────────────────────────────────────
// Elke getoonde functietitel is een link naar de bestaande CRM-selectie (het
// zoekraster van CandidateSearch) met een exact filter op de functie-identiteit:
// '#zoeken-<tab>?functie=<titel>' (lib/search/preset-url.mjs). De hash draagt de
// getoonde titel (functieWhitespace), zodat chip, kruimel en tabbladtitel hem tonen
// zoals de gebruiker hem zag; vergeleken wordt op functieIdentity uit
// lib/functie.mjs (de is-operator), nooit op een eigen spelling, zodat de link,
// het filter en de telling het nooit oneens kunnen zijn. De gebruiker wisselt
// daar zelf tussen Kandidaten, Contacten en Jobs en houdt het filter.
//
// De oude vorm '#functie-<sleutel>' (de losse functiepagina) blijft een geldige
// link: app/page.js vervangt hem met replaceState door de zoekhash, zodat Terug
// de alias overslaat. Eigen prefix, dus parseRecordHash raakt hem nooit.
export const FUNCTIE_HASH_RE = /^#functie-(.+)$/;
export const FUNCTIE_ROUTE_LABEL = 'Functie';

// '#zoeken-<tab>?functie=<titel>' of null bij een lege titel. Standaard de
// Kandidaten-tab; opts.entity ('candidate', 'contact', 'job') kiest een andere.
export function functieHash(title, opts = {}) {
  if (!functieIdentity(title)) return null;
  const tab = FUNCTIE_TAB[opts && opts.entity] || FUNCTIE_TAB.candidate;
  return buildSearchHash({ tab, functie: functieWhitespace(title) }) || null;
}

export function functieHref(title, opts = {}) {
  const h = functieHash(title, opts);
  return h ? `/${h}` : null;
}

// Inverse van de OUDE vorm '#functie-<sleutel>'. De identiteit wordt na het
// decoderen opnieuw toegepast, zodat een met de hand getypte hash met
// hoofdletters of dubbele spaties dezelfde sleutel geeft. Een kapotte codering
// ('%E0%A4%A') is geen route: null, nooit een halve sleutel. Voor de nieuwe vorm:
// parseSearchHash(h).preset.functie.
export function parseFunctieHash(h) {
  const s = normalizeHash(h);
  if (!s) return null;
  const m = s.match(FUNCTIE_HASH_RE);
  if (!m) return null;
  let decoded;
  try { decoded = decodeURIComponent(m[1]); }
  catch { return null; }
  const key = functieIdentity(decoded);
  return key ? { key } : null;
}

function workspaceKey(record) {
  const entity = String(record?.entity || '');
  if (entity === 'company') {
    return isClientType(record?.client_type) ? WORKSPACE_KEY.company : WORKSPACE_KEY.companyProspect;
  }
  return WORKSPACE_KEY[entity] || null;
}

// Bij welke werkruimte hoort dit record? Onbekend recordtype -> null.
export function canonicalWorkspaceHash(record) {
  const key = workspaceKey(record);
  const hash = key ? CRM_NAV_HASH[key] : null;
  return hash ? `#${hash}` : null;
}

export function canonicalWorkspaceLabel(record) {
  const key = workspaceKey(record);
  return key ? (WORKSPACE_LABEL[key] || null) : null;
}

// ── Labels van navigatie-affordances (terugpijl, contextstrook, kruimelpad) ──
// EEN bron voor "waar brengt deze knop me heen", zodat de randpijl en het
// kruimelpad nooit twee verschillende strings tonen voor hetzelfde doel.
//
// Volgorde: 1) de naam van het BOVENLIGGENDE RECORD, 2) het exacte label van de
// werkruimte van herkomst, 3) het canonieke werkruimte-label van dit recordtype,
// 4) niets -> de knop heet 'Sluiten'.
//
// Twee vangnetten, hier en niet bij de call sites:
//  - een leeg label telt niet mee (val door naar de volgende prioriteit);
//  - het woord 'Terug' zelf telt niet mee. Anders levert de oude tabLabel-fallback
//    letterlijk 'Terug naar Terug' op.
//
// Retour: { mode, label }. mode 'back' = 'Terug naar {label}', mode 'to' =
// 'Naar {label}' (deep link zonder oorsprong in de app: je gaat ergens HEEN, je
// keert niet terug), mode 'close' = 'Sluiten' (label is dan null).
const GENERIC_BACK_WORDS = Object.freeze(['terug', 'back']);

function usableLabel(s) {
  const v = String(s == null ? '' : s).trim();
  if (!v) return null;
  if (GENERIC_BACK_WORDS.includes(v.toLowerCase())) return null;
  return v;
}

export function navigationLabel(ctx) {
  const c = ctx && typeof ctx === 'object' ? ctx : {};
  const canonical = c.canonicalLabel != null
    ? c.canonicalLabel
    : (c.record ? canonicalWorkspaceLabel(c.record) : null);
  const label = usableLabel(c.parentRecordLabel)
    || usableLabel(c.sourceWorkspaceLabel)
    || usableLabel(canonical);
  if (!label) return { mode: 'close', label: null };
  // Zonder oorsprong in de app (verse deep link) is er niets om naar terug te
  // keren; de knop benoemt dan de bestemming i.p.v. een herkomst.
  return { mode: c.hasOrigin === false ? 'to' : 'back', label };
}

// history.state-metadata. MERGET altijd: { pbModal } van lib/modal-history.mjs
// (en Next.js-router-internals) moeten intact blijven, anders breekt hasMarker().
export function withRoute(state, pbRoute) {
  const base = state && typeof state === 'object' ? state : null;
  return { ...base, [PB_ROUTE_STATE_KEY]: pbRoute ?? null };
}

export function readRoute(state) {
  if (!state || typeof state !== 'object') return null;
  return state[PB_ROUTE_STATE_KEY] || null;
}

// ── Herkomst van een entry ───────────────────────────────────────────────────
// De lege string is een GELDIGE hash: dat is Vandaag (geen fragment). Een kale
// truthiness-test op originHash zag Vandaag daarom aan voor "geen herkomst",
// waardoor een record dat vanaf Vandaag openging nooit terug kon. Vandaar deze
// drie helpers: alleen null/undefined betekent hier "geen herkomst".
export function hasRouteOrigin(route) {
  return route != null && typeof route === 'object' && route.originHash != null;
}

export function routeOriginHash(route) {
  return hasRouteOrigin(route) ? normalizeHash(route.originHash) : null;
}

// Hoeveel history-stappen kost het om de hele detailketen te verlaten? 0 =
// springen kan niet, de aanroeper valt terug op de canonieke werkruimte.
export function historyBackSteps(route) {
  const depth = Number(route?.depth) || 0;
  return depth > 0 && hasRouteOrigin(route) ? depth : 0;
}

// ── Hoe schrijft de router deze hash weg? ────────────────────────────────────
// 'skip'    niets te doen (zelfde hash, entry heeft al route-metadata)
// 'stamp'   zelfde hash, maar de entry mist metadata: replaceState op dezelfde
//           URL zodat een verse deep-link-entry alsnog zijn pbRoute krijgt.
//           NOOIT een push: dat zou een identieke dubbele entry opleveren.
// 'push'    echte nieuwe entry
// 'replace' correctie op de huidige entry (boot-redirect, of push nog niet vrij)
export function hashWriteMode({ currentHash, targetHash, push, historyReady, hasRoute }) {
  if (normalizeHash(currentHash) === normalizeHash(targetHash)) return hasRoute ? 'skip' : 'stamp';
  return (push && historyReady) ? 'push' : 'replace';
}

// Canonieke CRM-record-hash van een deep link, of null. Processen vallen er
// bewust buiten: die hebben hun eigen deep-link-afhandeling (deepLinkDealIdRef).
export function crmDeepLinkHash(hash) {
  // Het kwalificatieformulier heeft dezelfde bescherming nodig: een verse
  // #kwal--deeplink mag niet door de tab-writer worden overschreven voordat de
  // pagina hem heeft opgepakt. Genormaliseerd, dus '/#kwal-…' geeft dezelfde sleutel.
  if (parseQualHash(hash)) return normalizeHash(hash);
  // De CV-pagina idem: een verse #cv--deeplink blijft staan tot de pagina hem heeft.
  if (parseCvHash(hash)) return normalizeHash(hash);
  // Match heeft dezelfde bescherming nodig: een verse #match-job--deeplink
  // mag niet door de tab-writer naar '#match' worden platgeslagen voordat de
  // pagina zijn route heeft opgepakt.
  if (parseMatchHash(hash)) return normalizeHash(hash);
  // De oude functiehash idem: een verse #functie--deeplink mag niet door de
  // tab-writer worden overschreven voordat app/page.js hem naar de zoekhash heeft
  // omgezet. De nieuwe vorm (#zoeken-<tab>?functie=) valt hier bewust buiten: die
  // beschermt urlPresetRouteRef, en deze ref wordt alleen door een recordvenster
  // gewist, dus hij zou de tab-writer op de functiehash vastzetten.
  if (parseFunctieHash(hash)) return normalizeHash(hash);
  const rec = parseRecordHash(hash);
  if (!rec || rec.entity === 'process') return null;
  return recordHash(rec.entity, rec.slug);
}

// Een railklik naar de CRM-werkruimte schrijft eerst de kale werkruimte-hash
// (de werkruimte meldt zijn echte subpagina pas een effect later terug) en
// daarna de exacte subpagina-hash. Dat waren twee history-entries voor één klik.
// De eerste schrijfactie is daarom VOORLOPIG: de tweede vervangt hem.
export function workspaceHashWrite({ genericHash, exactHash, provisional }) {
  const exact = normalizeHash(exactHash);
  if (exact) return { hash: exact, push: !provisional, provisional: false };
  return { hash: normalizeHash(genericHash), push: true, provisional: true };
}

// ── seq: de positie van een entry in de history-lijst ────────────────────────
// Elke pbRoute draagt een positief geheel getal, oplopend met de echte volgorde
// van de entries. Een push mint een nieuw nummer; replace en stamp HERGEBRUIKEN
// het nummer dat de entry al had, anders zou een correctie de entry verplaatsen
// terwijl hij op zijn plek blijft staan. Retour: het nummer plus de nieuwe stand
// van de teller, zodat de aanroeper geen tweede rekensom hoeft te doen.
export function nextRouteSeq(currentSeq, mode, existingSeq) {
  const raw = Number(currentSeq);
  const cur = Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : 0;
  if (mode === 'skip') return { seq: null, seqCounter: cur };
  if (mode === 'replace' || mode === 'stamp') {
    const keep = Number(existingSeq);
    if (Number.isFinite(keep) && keep > 0) {
      const k = Math.floor(keep);
      return { seq: k, seqCounter: Math.max(cur, k) };
    }
  }
  // After Back, a push replaces the forward branch. Its position is one after
  // the CURRENT entry, not one after the highest number ever allocated.
  const at = Number(existingSeq);
  const base = mode === 'push' && Number.isFinite(at) && at > 0 ? Math.floor(at) : cur;
  const next = base + 1;
  return { seq: next, seqCounter: next };
}

export function historyBackLabel(mode, existing, previousLabel = null) {
  return mode === 'push' ? (previousLabel || existing?.label || null) : (existing?.backLabel || null);
}

// ── Beoordeling van een history-sprong ───────────────────────────────────────
// Een diepte is alleen bekend als de entry er ECHT een getal voor draagt. Een
// kale Number(null) is 0 en zou 'onbekend' stilletjes in 'de werkruimte' laten
// omvallen, precies de verwarring die deze ronde moet opruimen.
function routeDepth(v) {
  return (typeof v === 'number' && Number.isFinite(v)) ? v : null;
}


// history.go() is asynchroon en kan stilletjes te weinig doen (entries vervangen,
// hersteld tabblad). Het oude vangnet keek 250 ms later of er nog een RECORD-hash
// stond en corrigeerde dan; een sprong naar een OUDERRECORD eindigt per definitie
// op een record-hash, dus dat net ving elke geldige kruimelklik. Vandaar een
// oordeel op de echte diepte i.p.v. op de vorm van de hash.
//
// 'ok'       we staan waar we wilden staan
// 'short'    we staan te diep: remaining = hoeveel stappen er nog te gaan zijn
// 'overshot' we staan te ondiep: NIETS doen. Vooruitduwen naar de canonieke
//            werkruimte was precies de sprong die de gebruiker uit zijn keten
//            gooide; een stap te ver terug is hinderlijk, een gedwongen
//            vooruitsprong is dataverlies van context.
// 'stuck'    de browser deed niets, of de entry is te oud om iets te bewijzen
//            terwijl we duidelijk nog in een detailvenster staan.
export function walkVerdict({ popped, targetDepth, landedDepth, landedIsRecord }) {
  if (!popped) return { status: 'stuck', remaining: 0 };
  const target = Math.max(0, Number(targetDepth) || 0);
  const landed = routeDepth(landedDepth);
  // Een entry van vóór deze router draagt geen depth. Onbekend is NIET 0: we
  // trekken een gebruiker nooit van een pagina waarvan we niet kunnen bewijzen
  // dat hij verkeerd is. Alleen "we wilden de keten uit en staan nog op een
  // record" is bewijs genoeg.
  if (landed == null) {
    return (target === 0 && landedIsRecord)
      ? { status: 'stuck', remaining: 0 }
      : { status: 'ok', remaining: 0 };
  }
  if (landed === target) return { status: 'ok', remaining: 0 };
  if (landed > target) return { status: 'short', remaining: landed - target };
  return { status: 'overshot', remaining: 0 };
}

// Wat doet de history-loper met de entry waarop hij landde? 'accept', 'retry' (nog
// verder terug) of 'correct' (direct naar de herkomst). Een sprong naar de wortel
// met een bekende hash controleert die hash; diepte alleen kan een ander raster
// goedkeuren. `exact` (de herkomst is een URL-preset, #processen?<query>): dan telt
// ALLEEN die volledige hash, query inbegrepen. Twee preset-entries delen hun
// basis-hash maar zijn twee verschillende rasters, en verder teruglopen landt op
// een ouder raster (browserronde L, 4 okt 2026). Dus geen tweede sprong: corrigeren.
export function rootLandingAction({ verdict, targetDepth, targetHash, landedHash, retried = false, exact = false }) {
  const toRoot = (Number(targetDepth) || 0) === 0 && targetHash != null;
  const onTarget = toRoot && normalizeHash(landedHash) === normalizeHash(targetHash);
  if (toRoot && exact) return onTarget ? 'accept' : 'correct';
  const status = verdict && verdict.status;
  if (toRoot && (status === 'ok' || status === 'overshot')) return onTarget ? 'accept' : 'correct';
  if (status === 'ok' || status === 'overshot') return 'accept';
  if (status === 'short' && !retried) return 'retry';
  return 'correct';
}

// Waar corrigeren we naartoe als de sprong faalde? De herkomst van de entry die
// we verlieten wint van het canonieke werkruimte-adres: dáár kwam de keten echt
// vandaan. De LEGE STRING is een geldige herkomst (Vandaag), dus de test is op
// null en niet op falsy.
export function correctionTarget({ originHash, record }) {
  if (originHash != null) return { hash: normalizeHash(originHash), source: 'origin' };
  const canonical = canonicalWorkspaceHash(record);
  if (canonical != null) return { hash: canonical, source: 'canonical' };
  return { hash: null, source: 'none' };
}

// ── Ketendiepte: uit de HISTORY-ENTRY, niet uit de React-stapels ─────────────
// De oude telling was globalProfileStack.length + 1 (of detailBack.length + 1).
// Die stapels leven per VENSTER: de overgang CRM -> proces leegt de profielstapel
// en zet één kruimel terug, dus na klanten > bedrijf > job > proces telde hij 2
// terwijl de echte afstand tot de werkruimte 3 is. De history weet dat wél.
//
//   geen detailvenster open           -> 0 (we staan op een werkruimte)
//   de eigen hash van het venster staat al in de URL -> de diepte van díe entry
//   anders (de push moet nog gebeuren) -> de huidige diepte + 1
//   geen bruikbare metadata           -> de gemeten stapeltelling, minimaal 1
export function chainDepthForRender({ hasWindow, windowHash, currentHash, currentDepth, measuredDepth }) {
  if (!hasWindow) return 0;
  const measured = Math.max(1, Number(measuredDepth) || 0);
  const own = normalizeHash(windowHash);
  const cur = routeDepth(currentDepth);
  if (!own || cur == null || cur < 0) return measured;
  return own === normalizeHash(currentHash) ? Math.max(1, cur) : cur + 1;
}

// ── Kruimelpad van een detailvenster ─────────────────────────────────────────
// EEN vorm voor BEIDE vensterfamilies (record en proces): de werkruimte van
// herkomst als wortel (diepte 0), elke doorgeklikte tussenstap, en het venster
// zelf als laatste kruimel. Het procesvenster had hiervoor een eigen model met
// precies één terugkruimel, gegate op een backTarget die de meeste ingangen niet
// zetten; daardoor stond het venster kruimelloos in beeld.
//
// De kruimels dragen de ECHTE history-diepte, want klikken doet
// go(-(huidige diepte - die diepte)). De stapels leven per venster, dus de
// afstand tussen de wortel en dit venster kan groter zijn dan de stapel lang is
// (bijvoorbeeld met een proces onder de keten). Dat verschil is `base`, de
// bodem waarop de tussenkruimels verschuiven.
//
// mids bevat ALLEEN echte tussenvensters. Een kruimel die de werkruimte zelf
// voorstelt hoort in rootLabel, niet in mids: hij heeft geen eigen history-entry
// en zou de diepte van alles erna een stap opschuiven.
//
// rootQual en rootGlyph horen bij de HERKOMST van het venster (recordvenster v7):
// kende de opener de lijst waaruit dit venster opende, dan draagt de wortel naast
// zijn naam ook een kwalificatie ('Bellijst' + 'Finance Q3') en een glyphnaam.
// Puur weergavedata voor de titelbalk; ze raken de dieptes niet. Zonder herkomst
// staan de sleutels er niet eens op en is de wortel byte voor byte de oude.
export function buildDetailTrail({ rootLabel, rootQual = null, rootGlyph = null, rootGuessed = false, mids = [], lastLabel, chainDepth = 0 }) {
  const list = Array.isArray(mids) ? mids : [];
  const base = Math.max(0, (Number(chainDepth) || 0) - (list.length + 1));
  return [
    {
      label: rootLabel || '',
      depth: 0,
      guessed: !!rootGuessed,
      ...(rootQual ? { qual: rootQual } : {}),
      ...(rootGlyph ? { glyph: rootGlyph } : {}),
    },
    // qual en glyph reizen ook op een TUSSENkruimel mee: een record dat uit een
    // procesvenster opende zet dat proces als eerste tussenstap ('Interviewproces,
    // Haystaq' met de takglyph). Zonder herkomst staan de sleutels er niet op en
    // is de kruimel byte voor byte de oude.
    ...list.map((m, i) => ({
      label: (m && m.label) || '',
      depth: base + i + 1,
      ...(m && m.qual ? { qual: m.qual } : {}),
      ...(m && m.glyph ? { glyph: m.glyph } : {}),
    })),
    { label: lastLabel || '', depth: base + list.length + 1 },
  ];
}

// ── Richting van een traversal ───────────────────────────────────────────────
// seq loopt op met de echte volgorde van de entries, dus een vergelijking zegt
// of de gebruiker terug of vooruit stapte. Gelijk = een modal-entry die de seq
// van de entry eronder deelt. Onbekend = een entry zonder metadata; dan gokken
// we niet.
export function traversalDirection(prevRoute, landedRoute) {
  const a = usableSeq(prevRoute?.seq);
  const b = usableSeq(landedRoute?.seq);
  if (a == null || b == null) return 'unknown';
  if (b < a) return 'back';
  if (b > a) return 'forward';
  return 'same';
}

// De vooruit-kruimels na een traversal. Retourneert DEZELFDE array-referentie
// als er niets verandert, zodat de aanroeper zijn setState kan overslaan.
export function forwardTrailAfterTraversal({ trail, direction, leavingLabel, selfInitiated }) {
  const list = Array.isArray(trail) ? trail : [];
  // De app sprong zelf: die boekte zijn kruimel al bij het vertrek. Nog een keer
  // boeken zou de pijl een niveau te diep laten wijzen.
  if (selfInitiated) return list;
  if (direction === 'back') return [...list, leavingLabel || null];
  if (direction === 'forward') return list.length ? list.slice(0, -1) : list;
  if (direction === 'same') return list;
  // Onbekende richting: liever geen pijl dan een pijl die liegt.
  return list.length ? [] : list;
}

// ── De ketenstapels verzoenen met de entry waarop we landen ──────────────────
// Een snapshot is het enige dat een stapel kan HERSTELLEN, en dat heeft een
// vooruitstap nodig: de drill-in-stapel die bij die entry hoorde is bij het
// terugstappen weggegooid. Zonder snapshot kunnen we alleen KRIMPEN: een landing
// op diepte d heeft precies d-1 voorouders boven de werkruimte. Groeien op basis
// van een diepte zou een kruimel verzinnen die we niet kunnen benoemen.
export function reconcileChainOnLanding({ landedDepth, snapshot, profileStack, detailBack }) {
  if (snapshot && Array.isArray(snapshot.profileStack) && Array.isArray(snapshot.detailBack)) {
    return { profileStack: snapshot.profileStack, detailBack: snapshot.detailBack };
  }
  const d = routeDepth(landedDepth);
  if (d == null) return { profileStack, detailBack };
  const ancestors = Math.max(0, d - 1);
  const shrink = (list) => (Array.isArray(list) && list.length > ancestors ? list.slice(0, ancestors) : list);
  return { profileStack: shrink(profileStack), detailBack: shrink(detailBack) };
}

// ── Ketensnapshots per entry ─────────────────────────────────────────────────
// Onveranderlijke ring op seq: de entry waar we NU op staan schrijft zijn stapels
// weg, zodat een latere traversal ze terug kan zetten. Een tweede schrijfactie op
// dezelfde entry vervangt (de entry verhuist niet), de oudste valt af boven het
// plafond.
//
// Een sleutel is alleen bruikbaar als hij een POSITIEF getal is: nextRouteSeq
// mint vanaf 1, en een kale Number(null) is 0 en zou anders als geldige sleutel
// binnenglippen waar 'deze entry heeft geen seq' bedoeld was.
function usableSeq(seq) {
  const s = Number(seq);
  return (Number.isFinite(s) && s > 0) ? s : null;
}

export function putChainSnapshot(list, seq, snapshot, limit) {
  const arr = Array.isArray(list) ? list : [];
  const s = usableSeq(seq);
  if (s == null) return arr;
  const rawCap = Number(limit);
  const cap = Number.isFinite(rawCap) && rawCap > 0 ? Math.floor(rawCap) : 40;
  const idx = arr.findIndex((e) => e && Number(e.seq) === s);
  if (idx >= 0) {
    const next = arr.slice();
    next[idx] = { seq: s, snapshot };
    return next;
  }
  const next = [...arr, { seq: s, snapshot }];
  return next.length > cap ? next.slice(next.length - cap) : next;
}

export function findChainSnapshot(list, seq) {
  const s = usableSeq(seq);
  if (s == null || !Array.isArray(list)) return null;
  const hit = list.find((e) => e && Number(e.seq) === s);
  return hit ? (hit.snapshot ?? null) : null;
}
